// Connect your iCloud calendar (CalDAV). Apple has no calendar OAuth, so the user
// creates an app-specific password at appleid.apple.com and pastes it here with
// their Apple ID. Reached after email sign-in when there's no calendar yet.
// Mirrors the portal's AppleConnectModal in the when brand, with the connect
// playbook: three steps, deep link, whitespace trim, scoped/revocable reassurance,
// specific errors, funnel events.
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { connectApple, syncNow } from '../services/calendar';
import { ensureDefaultSchedule } from '../services/schedule';
import { capture } from '../lib/analytics';
import { Screen, Card, Eyebrow, Title, Button, TextInput } from '../components/ui';

const NEXT_KEY = 'when_next';
const APPLE_PASSWORD_URL = 'https://account.apple.com/account/manage';
const APPLE_HELP_URL = 'https://support.apple.com/102654';

function Step({ n, children }) {
  return (
    <li className="flex gap-3">
      <span className="flex h-6 w-6 shrink-0 items-center justify-center border border-line font-mono text-xs">{n}</span>
      <span className="font-sans text-sm text-ink/80">{children}</span>
    </li>
  );
}

export default function ConnectCalendar() {
  const navigate = useNavigate();
  const [appleId, setAppleId] = useState('');
  const [appPassword, setAppPassword] = useState('');
  const [status, setStatus] = useState('idle'); // idle | connecting | error
  const [error, setError] = useState(null);

  useEffect(() => { capture('icloud_connect_viewed'); }, []);

  const resume = async () => {
    // First-run tail mirrors AuthCallback: ensure a schedule, kick a sync, go on.
    await ensureDefaultSchedule().catch(() => {});
    syncNow().catch(() => {});
    const next = sessionStorage.getItem(NEXT_KEY);
    sessionStorage.removeItem(NEXT_KEY);
    navigate(next || '/home', { replace: true });
  };

  const submit = async (e) => {
    e.preventDefault();
    const id = appleId.trim();
    const pw = appPassword.trim();
    if (!id || !pw) { setError('Enter your Apple ID and app-specific password.'); return; }

    setStatus('connecting'); setError(null);
    capture('icloud_connect_submitted');
    try {
      const res = await connectApple({ appleId: id, appPassword: pw });
      capture('icloud_connect_succeeded', { calendar_count: res?.calendars?.length ?? 0 });
      capture('calendar_connected', { provider: 'apple' });
      await resume();
    } catch (err) {
      const reason = err?.status === 401 ? 'invalid_credentials' : err?.status === 422 ? 'no_calendars' : 'error';
      capture('icloud_connect_failed', { reason });
      setError(
        err?.status === 401
          ? 'Couldn\'t sign in to iCloud. Check your Apple ID and that you used an app-specific password — not your normal Apple password.'
          : err?.status === 422
            ? 'Signed in, but no iCloud calendars were found to sync.'
            : 'Couldn\'t connect iCloud. Try again.'
      );
      setStatus('error');
    }
  };

  const connecting = status === 'connecting';

  return (
    <Screen>
      <Card>
        <Eyebrow>connect your calendar</Eyebrow>
        <Title className="mt-4 text-2xl">Connect iCloud.</Title>
        <p className="mt-4 font-sans text-base text-ink/70">
          Apple doesn't use a sign-in popup. Create a one-off app-specific password for
          Vennio, then paste it below.
        </p>

        <ol className="mt-6 space-y-3">
          <Step n="1">
            <a href={APPLE_PASSWORD_URL} target="_blank" rel="noopener noreferrer" className="text-accent underline">
              Open Apple → Sign-In &amp; Security
            </a>{' '}
            and create an app-specific password.{' '}
            <a href={APPLE_HELP_URL} target="_blank" rel="noopener noreferrer" className="text-ink/50 underline">
              How?
            </a>
          </Step>
          <Step n="2">Copy the 16-character password it shows you.</Step>
          <Step n="3">Paste it below with your Apple ID.</Step>
        </ol>

        <form onSubmit={submit} className="mt-6 space-y-4">
          <TextInput
            id="apple-id"
            type="email"
            label="Apple ID"
            value={appleId}
            onChange={(e) => setAppleId(e.target.value)}
            placeholder="you@icloud.com"
            autoComplete="username"
            disabled={connecting}
            required
          />
          <TextInput
            id="apple-app-password"
            type="password"
            label="App-specific password"
            value={appPassword}
            onChange={(e) => setAppPassword(e.target.value)}
            placeholder="abcd-efgh-ijkl-mnop"
            autoComplete="off"
            hint="16 characters — not your normal Apple password."
            disabled={connecting}
            required
          />

          <p className="font-mono text-xs text-ink/50">
            This password only lets Vennio read your calendar's busy times. Revoke it anytime
            at appleid.apple.com — it never affects your Apple account password.
          </p>

          {error && <p className="font-mono text-xs text-red-700">{error}</p>}

          <Button type="submit" disabled={connecting || !appleId.trim() || !appPassword.trim()}>
            {connecting ? 'Connecting…' : 'Connect iCloud'}
          </Button>
        </form>
      </Card>
    </Screen>
  );
}
